import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Smartphone, Users, Info, Mail, HelpCircle, ShoppingBag } from "lucide-react";
import { cn } from "../lib/utils";

export default function Navbar() {
  const [activeTab, setActiveTab] = useState("Home");
  const [isMobile, setIsMobile] = useState(false);

  const navItems = [
    { name: "Home", url: "#home", icon: Play },
    { name: "Features", url: "#features", icon: Smartphone },
    { name: "How It Works", url: "#how-it-works", icon: Users },
    { name: "Shop", url: "#shop", icon: ShoppingBag },
    { name: "About", url: "#about", icon: Info },
    { name: "FAQs", url: "#contact", icon: HelpCircle },
    { name: "Contact", url: "#contact", icon: Mail },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => ({
        name: item.name,
        element: document.querySelector(item.url)
      }));

      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveTab(section.name);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (url, name) => {
    if (name === "Shop") {
      // Open shop login page in new tab
      window.open("https://vendor.vreels.com/", "_blank");
      return;
    }
    
    const element = document.querySelector(url);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setActiveTab(name);
  };

  return (
    <>
      {/* Logo */}
      <div className="fixed top-4 left-4 z-50">
        <a 
          href="#home" 
          onClick={(e) => { e.preventDefault(); scrollToSection('#home', 'Home'); }} 
          className="flex items-center space-x-2"
        >
          <img 
            src="/images/logo_new_nobg.png" 
            alt="VREELS" 
            className="h-8 w-auto"
            style={{
              filter: 'drop-shadow(0 0 8px rgba(254, 62, 6, 0.5)) brightness(1.1) contrast(1.1)'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          />
          <span 
            className="text-xl font-bold gradient-text-brand"
            style={{ display: 'none' }}
          >
            VREELS
          </span>
        </a>
      </div>

      {/* Floating Navigation */}
      <div className="fixed bottom-4 sm:top-6 left-0 sm:left-1/2 sm:-translate-x-1/2 w-full sm:w-auto px-4 sm:px-0 z-50 pointer-events-none">
        <div className="flex items-center sm:gap-3 gap-0.5 justify-between sm:justify-center bg-background/80 border border-border backdrop-blur-lg py-2 px-1 sm:px-2 rounded-full shadow-lg pointer-events-auto overflow-x-auto no-scrollbar">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.name;

            return (
              <button
                key={item.name}
                onClick={() => scrollToSection(item.url, item.name)}
                className={cn(
                  "relative cursor-pointer text-sm font-semibold px-2 sm:px-5 py-2 rounded-full transition-all duration-200 whitespace-nowrap",
                  "text-foreground/80 hover:text-primary",
                  isActive && "text-primary",
                )}
              >
                <span className="hidden md:inline">{item.name}</span>
                <span className="md:hidden">
                  <Icon size={18} strokeWidth={2.5} />
                </span>
                {isActive && (
                  <motion.div
                    layoutId="navbar-pill"
                    className="absolute inset-0 w-full bg-primary/10 rounded-full -z-10 border border-primary/20"
                    initial={false}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }}
                  >
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full">
                      <div className="absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2" />
                      <div className="absolute w-8 h-6 bg-primary/20 rounded-full blur-md -top-1" />
                      <div className="absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2" />
                    </div>
                  </motion.div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
